/*
 * THESIS: the conversion moment IS the product experience; a visitor does not
 *   fill a contact form, they open a work item on the firm's board, refusing
 *   the name/email/message rectangle every services site ships.
 * OWN-WORLD: Maestro card grammar drawn live in CSS: lane chips in the three
 *   accents, a card that composes itself as you type, and a state change to
 *   ON THE BOARD with the response chip when it lands.
 * STORY: pick the lane, name the pain in one line, watch it become a card,
 *   put it on the board; the confirmation is the card in its new state.
 * FIRST VIEWPORT: headline, lane chips and the live card side by side; the
 *   booking line above the fold for people who want a calendar instead.
 * FORM: split composer + live card preview; pinned direction, no seed.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Seo from '../Seo'
import SiteAmbient from '../common/SiteAmbient'
import trackEvent from '../../utils/trackEvent'
import { getTalkContent, INTERESTS } from '../../content/talkContent'

const CONTACT_API = (process.env.REACT_APP_API_BASE_URL || 'https://app.leanit101.com')
  .replace(/\/$/, '')
  // plain http to the API dies in CORS preflight (nginx 301s to https)
  .replace(/^http:\/\/(?=[^/]*leanit101\.com)/, 'https://')
const BOOKING_URL = process.env.REACT_APP_BOOKING_URL || '/book'

const ACCENT = {
  1: { chip: 'border-pyramid-1/50 text-pyramid-1', active: 'bg-pyramid-1 text-[#101418] border-pyramid-1', tag: 'bg-pyramid-1/15 text-pyramid-1' },
  10: { chip: 'border-pyramid-10/50 text-pyramid-10', active: 'bg-pyramid-10 text-[#101418] border-pyramid-10', tag: 'bg-pyramid-10/15 text-pyramid-10' },
  19: { chip: 'border-pyramid-19/50 text-pyramid-19', active: 'bg-pyramid-19 text-[#101418] border-pyramid-19', tag: 'bg-pyramid-19/15 text-pyramid-19' },
}

const TalkPage = () => {
  const { i18n } = useTranslation()
  const c = getTalkContent(i18n.language)

  const initialInterest = useMemo(() => {
    if (typeof window === 'undefined') return 'fde'
    const q = new URLSearchParams(window.location.search).get('i')
    return INTERESTS.some((x) => x.id === q) ? q : 'fde'
  }, [])

  const [interest, setInterest] = useState(initialInterest)
  const [title, setTitle] = useState('')
  const [detail, setDetail] = useState('')
  // honeypot: humans never see it, bots fill it, the server drops those
  const [trap, setTrap] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [status, setStatus] = useState('idle')
  const [error, setError] = useState('')
  const viewTracked = useRef(false)

  useEffect(() => {
    if (viewTracked.current) return
    viewTracked.current = true
    trackEvent('funnel_talk_view', { interest: initialInterest })
  }, [initialInterest])

  const pickLane = (id) => {
    setInterest(id)
    trackEvent('funnel_talk_lane', { interest: id })
  }

  const accent = ACCENT[(INTERESTS.find((x) => x.id === interest) || {}).accent || 1]

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    if (!name.trim() || !email.trim() || !title.trim()) {
      setError(c.form.errorRequired)
      return
    }
    setStatus('sending')
    const message = `[${c.interests[interest]}] ${title.trim()}${detail.trim() ? `\n\n${detail.trim()}` : ''}`
    const source = `talk:${interest}`
    const payload = {
      name: name.trim(),
      fullName: name.trim(),
      email: email.trim(),
      workEmail: email.trim(),
      company: company.trim(),
      companyName: company.trim(),
      message,
      notes: message,
      source,
      website: trap,
      formData: { name: name.trim(), email: email.trim(), company: company.trim(), message, source, interest },
      contact: { name: name.trim(), email: email.trim(), company: company.trim(), message },
    }
    try {
      const res = await fetch(`${CONTACT_API}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const bodyText = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status} ${bodyText.slice(0, 140)}`)
      }
      setStatus('done')
      trackEvent('funnel_talk_submit', { interest })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[talk] submit failed', err)
      trackEvent('funnel_talk_error', { interest, message: String(err && err.message).slice(0, 120) })
      setStatus('idle')
      setError(c.form.errorSend)
    }
  }

  const inputCls =
    'w-full rounded-lg border border-white/[0.12] bg-[#151b23] px-4 py-3 text-[15px] text-white placeholder:text-slate-500 transition-colors focus:border-pyramid-19/60 focus:outline-none focus:ring-2 focus:ring-pyramid-19/20'

  return (
    <div className="relative isolate bg-transparent font-Quicksand text-white">
      <SiteAmbient />
      <Seo title={c.seo.title} description={c.seo.description} path="/talk" />

      <header className="px-6 pb-4 pt-32 md:px-16 md:pt-40">
        <div className="mx-auto max-w-6xl">
          <p className="text-xs uppercase tracking-[0.4em] text-pyramid-1/75">{c.hero.kicker}</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-light leading-[1.05] md:text-6xl">{c.hero.headline}</h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">{c.hero.body}</p>
          {BOOKING_URL ? (
            <p className="mt-5 text-sm text-slate-400">
              {c.booking.lead}{' '}
              <a
                href={BOOKING_URL}
                target={BOOKING_URL.startsWith('/') ? undefined : '_blank'}
                rel='noopener noreferrer'
                onClick={() => trackEvent('funnel_talk_booking_click', { interest })}
                className="font-semibold text-pyramid-19 underline underline-offset-4"
              >
                {c.booking.cta}
              </a>
            </p>
          ) : null}
        </div>
      </header>

      <main className="px-6 pb-24 pt-8 md:px-16">
        <div className="mx-auto grid max-w-6xl items-start gap-10 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
          {/* ── composer ── */}
          <form onSubmit={submit} className="space-y-6" noValidate>
            <input type='text' name='website' value={trap} onChange={(e) => setTrap(e.target.value)} tabIndex={-1} autoComplete='off' aria-hidden='true' style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }} />
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{c.form.laneLabel}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {INTERESTS.map((it) => {
                  const a = ACCENT[it.accent]
                  const isActive = interest === it.id
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => pickLane(it.id)}
                      aria-pressed={isActive}
                      className={`rounded-md border px-3.5 py-2 text-[13px] font-semibold transition-all duration-200 ${
                        isActive ? a.active : `bg-transparent ${a.chip} hover:bg-white/[0.04]`
                      }`}
                    >
                      {c.interests[it.id]}
                    </button>
                  )
                })}
              </div>
            </div>

            <div>
              <label htmlFor="talk-title" className="text-xs uppercase tracking-[0.3em] text-slate-400">
                {c.form.titleLabel}
              </label>
              <input
                id="talk-title"
                type="text"
                maxLength={120}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={c.form.titlePlaceholder}
                className={`${inputCls} mt-3 text-lg font-semibold`}
                disabled={status !== 'idle'}
              />
            </div>

            <div>
              <label htmlFor="talk-detail" className="text-xs uppercase tracking-[0.3em] text-slate-400">
                {c.form.detailLabel}
              </label>
              <textarea
                id="talk-detail"
                rows={4}
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                placeholder={c.form.detailPlaceholder}
                className={`${inputCls} mt-3 resize-y`}
                disabled={status !== 'idle'}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label htmlFor="talk-name" className="text-xs uppercase tracking-[0.3em] text-slate-400">{c.form.nameLabel}</label>
                <input id="talk-name" type="text" value={name} onChange={(e) => setName(e.target.value)} className={`${inputCls} mt-3`} disabled={status !== 'idle'} />
              </div>
              <div>
                <label htmlFor="talk-email" className="text-xs uppercase tracking-[0.3em] text-slate-400">{c.form.emailLabel}</label>
                <input id="talk-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mt-3`} disabled={status !== 'idle'} />
              </div>
              <div>
                <label htmlFor="talk-company" className="text-xs uppercase tracking-[0.3em] text-slate-400">{c.form.companyLabel}</label>
                <input id="talk-company" type="text" value={company} onChange={(e) => setCompany(e.target.value)} className={`${inputCls} mt-3`} disabled={status !== 'idle'} />
              </div>
            </div>

            {error ? (
              <p role="alert" className="rounded-lg border border-pyramid-10/40 bg-pyramid-10/10 px-4 py-3 text-sm text-pyramid-10">
                {error}{' '}
                <a
                  className="font-semibold underline underline-offset-2"
                  href={`mailto:contact@leanit101.com?subject=${encodeURIComponent(`[${c.interests[interest]}] ${title}`)}&body=${encodeURIComponent(`${detail}\n\n${name} · ${company} · ${email}`)}`}
                >
                  contact@leanit101.com
                </a>
              </p>
            ) : null}

            {status !== 'done' ? (
              <button
                type="submit"
                disabled={status === 'sending'}
                className="inline-flex items-center gap-2 rounded-[10px] bg-gradient-to-r from-pyramid-1 via-pyramid-10 to-pyramid-19 px-7 py-3.5 font-semibold text-ebony shadow-lg shadow-pyramid-10/30 transition-all hover:-translate-y-0.5 disabled:opacity-60"
              >
                {status === 'sending' ? c.form.submitting : c.form.submit}
                <span aria-hidden="true">&#8594;</span>
              </button>
            ) : null}
            <p className="text-xs leading-5 text-slate-500">{c.form.privacy}</p>
          </form>

          {/* ── the live card ── */}
          <aside aria-live="polite">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{c.card.preview}</p>
            <div
              className={`mt-3 rounded-xl border bg-[#1a212a] p-5 shadow-2xl shadow-black/50 transition-all duration-500 ${
                status === 'done' ? 'border-pyramid-1/60 -translate-y-1' : 'border-white/[0.12]'
              }`}
              style={{ borderTopWidth: '3px' }}
            >
              {status === 'done' ? (
                <span className="inline-flex items-center rounded-md bg-pyramid-1/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-pyramid-1">
                  {c.card.submittedBadge}
                </span>
              ) : (
                <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${accent.tag}`}>
                  {c.interests[interest]}
                </span>
              )}
              <p className={`mt-3 text-lg font-semibold leading-snug ${title ? 'text-white' : 'text-slate-500'}`}>
                {title || c.card.untitled}
              </p>
              {detail ? <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-400">{detail}</p> : null}
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-white/[0.08] pt-4 text-[12px]">
                {status === 'done' ? (
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-pyramid-19/40 bg-pyramid-19/10 px-2.5 py-1 font-semibold text-pyramid-19">
                    {c.card.responseChip}
                  </span>
                ) : (
                  <span className="text-slate-500">
                    {(name || company) ? [name, company].filter(Boolean).join(' · ') : '—'}
                  </span>
                )}
                <span className="ml-auto text-slate-500">{c.card.waits}</span>
              </div>
            </div>

            {status === 'done' ? (
              <div className="pane-swap mt-5 rounded-xl border border-white/10 bg-white/[0.02] p-5">
                <p className="font-semibold text-white">{c.card.submittedTitle}</p>
                <p className="mt-2 text-sm leading-6 text-slate-400">{c.card.submittedBody}</p>
              </div>
            ) : null}
          </aside>
        </div>

        {/* ── FAQ ── */}
        <section className="mx-auto mt-20 max-w-6xl">
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{c.faq.kicker}</p>
          <div className="mt-6 max-w-3xl divide-y divide-white/[0.07] border-y border-white/[0.07]">
            {c.faq.items.map((f, i) => (
              <details key={f.q} className="group py-5">
                <summary className="flex cursor-pointer list-none items-baseline gap-5 marker:content-none [&::-webkit-details-marker]:hidden">
                  <span className="font-mono text-xs tabular-nums text-pyramid-19" aria-hidden="true">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="flex-1 text-[15px] font-light text-slate-200 transition-colors group-hover:text-white group-open:text-white">
                    {f.q}
                  </span>
                  <span
                    className="text-slate-500 transition-transform duration-200 group-open:rotate-45 group-open:text-pyramid-19"
                    aria-hidden="true"
                  >
                    +
                  </span>
                </summary>
                <p className="pane-swap mt-3 pl-[3.05rem] pr-8 text-sm font-light leading-7 text-slate-400">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
    </div>
  )
}

export default TalkPage
